import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle, 
  Star, 
  Phone, 
  MessageCircle, 
  Award, 
  Shield, 
  Clock, 
  MapPin, 
  Droplets, 
  Palette, 
  Wrench, 
  TrendingUp,
  ChevronDown,
  Quote,
  Zap,
  Heart
} from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';


const Section = styled.section`
  padding: 6rem 0;
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ServicesContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  color: #64748b;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const StatsSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  color: white;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;

  .number {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 800;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #FECE1B 0%, #ffffff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .label {
    font-size: 1.1rem;
    font-weight: 500;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    
    .number {
      font-size: clamp(2rem, 4.5vw, 3.5rem);
    }
    
    .label {
      font-size: 1rem;
    }
  }

  @media (max-width: 375px) {
    padding: 1rem;
    
    .number {
      font-size: clamp(1.8rem, 4vw, 3rem);
    }
    
    .label {
      font-size: 0.9rem;
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 3rem;
  margin-bottom: 4rem;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 2.5rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 0;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  height: fit-content;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.12);

    &::before {
      transform: scaleX(1);
    }
  }

  .service-image {
    width: 100%;
    height: 280px;
    object-fit: cover;
    border-radius: 20px 20px 0 0;
    transition: transform 0.4s ease;
  }

  &:hover .service-image {
    transform: scale(1.05);
  }

  .service-content {
    padding: 3rem;
  }

  .icon {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    color: #000000;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.3);
  }

  h3 {
    color: #000000;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
  }

  p {
    color: #64748b;
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  ul {
    list-style: none;
    margin: 1.5rem 0;
  }

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    color: #475569;
    font-weight: 500;

    svg {
      color: #10b981;
      flex-shrink: 0;
    }
  }

  @media (max-width: 768px) {
    .service-content {
      padding: 2.5rem;
    }
    
    .icon {
      width: 60px;
      height: 60px;
    }
    
    h3 {
      font-size: 1.375rem;
    }
  }

  @media (max-width: 375px) {
    .service-content {
      padding: 2rem;
    }
    
    .icon {
      width: 50px;
      height: 50px;
    }
    
    h3 {
      font-size: 1.25rem;
    }
    
    p {
      font-size: 0.9rem;
    }
    
    .service-image {
      height: 200px;
    }
  }
`;

const ProcessSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(254, 206, 27, 0.05) 0%, rgba(217, 119, 6, 0.03) 100%),
    linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: 3rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const ProcessCard = styled(motion.div)`
  text-align: center;
  position: relative;

  .step-number {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: #000000;
    font-size: 1.5rem;
    font-weight: 800;
    box-shadow: 0 15px 35px rgba(254, 206, 27, 0.3);
  }


  h3 {
    color: #000000;
    margin-bottom: 1rem;
    font-size: 1.375rem;
    font-weight: 700;
  }

  p {
    color: #64748b;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .step-number {
      width: 70px;
      height: 70px;
      font-size: 1.25rem;
    }
  }

  @media (max-width: 375px) {
    .step-number {
      width: 60px;
      height: 60px;
      font-size: 1.125rem;
    }
    
    h3 {
      font-size: 1.125rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const FeaturesSection = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: 3rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const FeatureCard = styled(motion.div)`
  text-align: center;
  padding: 2.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(254, 206, 27, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }

  .icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: #000000;
    position: relative;
    z-index: 2;
    box-shadow: 0 15px 35px rgba(254, 206, 27, 0.3);
  }

  h3 {
    margin-bottom: 1rem;
    color: #000000;
    font-size: 1.375rem;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }

  p {
    color: #64748b;
    line-height: 1.6;
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    
    .icon {
      width: 70px;
      height: 70px;
    }
  }

  @media (max-width: 375px) {
    padding: 1.5rem;
    
    .icon {
      width: 60px;
      height: 60px;
    }
    
    h3 {
      font-size: 1.125rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const TestimonialsSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
  color: white;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;

  .quote-icon {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    opacity: 0.3;
    font-size: 3rem;
  }

  .rating {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1.5rem;

    svg {
      color: #fbbf24;
      width: 20px;
      height: 20px;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    font-style: italic;
  }

  .reviewer {
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }

  .location {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
  }

  .project-type {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    margin-top: 1rem;
    display: inline-block;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    
    .quote-icon {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 375px) {
    padding: 1.5rem;
    
    .quote-icon {
      font-size: 2rem;
    }
    
    p {
      font-size: 0.9rem;
    }
    
    .reviewer {
      font-size: 1rem;
    }
    
    .location {
      font-size: 0.85rem;
    }
  }
`;

const GalleryPreviewSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(254, 206, 27, 0.03) 0%, rgba(217, 119, 6, 0.02) 100%),
    linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const GalleryCard = styled(motion.div)`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
  }

  img {
    width: 100%;
    height: 320px;
    object-fit: cover;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: 768px) {
      height: 280px;
    }

    @media (max-width: 375px) {
      height: 240px;
    }
  }

  &:hover img {
    transform: scale(1.08);
  }

  .overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
    color: white;
    padding: 2.5rem;
    transform: translateY(100%);
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
  }

  &:hover .overlay {
    transform: translateY(0);
  }

  h4 {
    font-size: 1.375rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
    color: #FECE1B;
  }

  p {
    opacity: 0.95;
    font-size: 1rem;
    line-height: 1.5;
  }

  /* Add a subtle glow effect on hover */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(254, 206, 27, 0.1) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  padding: 6rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 800px;
  margin: 3rem auto 0;
`;

const FAQItem = styled(motion.div)`
  background: #f8fafc;
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &.active {
    background: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .question {
    padding: 1.5rem;
    font-weight: 600;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #000000;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(254, 206, 27, 0.05);
    }

    svg {
      transition: transform 0.3s ease;
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 1.5rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #64748b;
    line-height: 1.6;

    &.active {
      padding: 0 1.5rem 1.5rem;
      max-height: 200px;
    }
  }
`;

const LocalAreasSection = styled.section`
  background: 
    linear-gradient(135deg, rgba(254, 206, 27, 0.04) 0%, rgba(217, 119, 6, 0.02) 100%),
    linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const AreasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-top: 2rem;
  }
`;

const AreaCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: #000000;

    @media (max-width: 375px) {
      width: 50px;
      height: 50px;
    }
  }

  h4 {
    color: #000000;
    margin-bottom: 0.5rem;
    font-weight: 700;

    @media (max-width: 375px) {
      font-size: 1rem;
    }
  }

  p {
    color: #64748b;
    font-size: 0.95rem;

    @media (max-width: 375px) {
      font-size: 0.85rem;
    }
  }
`;

const PartnershipSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const PartnershipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PartnershipCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  align-items: center;
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 2rem;
  }

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1.5rem;
  }
`;

const PartnershipLogo = styled.div`
  text-align: center;
  
  img {
    max-width: 150px;
    height: auto;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.1));
    
    @media (max-width: 768px) {
      max-width: 120px;
    }

    @media (max-width: 375px) {
      max-width: 100px;
    }
  }
`;

const PartnershipDetails = styled.div`
  h3 {
    color: #000000;
    margin-bottom: 0.5rem;
    font-size: 1.375rem;
    font-weight: 700;

    @media (max-width: 375px) {
      font-size: 1.25rem;
    }
  }

  .partner-tagline {
    color: #64748b;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 1rem;
    font-style: italic;
  }

  p {
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    font-size: 1rem;

    @media (max-width: 375px) {
      font-size: 0.9rem;
    }

    a {
      color: #FECE1B;
      text-decoration: none;
      font-weight: 600;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const PartnershipBenefits = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const PartnershipBenefit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #475569;
  font-weight: 500;
  font-size: 0.9rem;

  svg {
    color: #10b981;
    flex-shrink: 0;
  }
`;

const PartnershipCTA = styled.div`
  margin-top: 1rem;
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  background: rgba(254, 206, 27, 0.1);
  color: #FECE1B;
  border: 1px solid rgba(254, 206, 27, 0.2);

  &:hover {
    background: rgba(254, 206, 27, 0.15);
    border-color: rgba(254, 206, 27, 0.3);
    transform: translateY(-1px);
  }
`;

const CTA = styled.section`
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(26, 26, 26, 0.95) 100%),
    url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain-cta" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.3" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain-cta)"/></svg>');
  color: white;
  padding: 8rem 0 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }

  &.whatsapp {
    background: #25D366;
    color: white;
    box-shadow: 0 10px 30px rgba(37, 211, 102, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(37, 211, 102, 0.5);
    }
  }

  &.quote-button {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const HomePage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const services = [
    {
      title: 'Resin Driveways London',
      description: 'Transform your driveway with a resin bound surface that handles daily traffic, drains rainwater and elevates kerb appeal instantly.',
      features: ['Permeable & SUDS compliant', '10+ year warranty', 'Vehicle-ready within 24 hours', 'Custom borders & edging'],
      link: '/resin-driveways-london/',
      icon: <Droplets size={32} />,
      image: '/images/resin-surfaces-bound-work1.jpeg'
    },
    {
      title: 'Resin Patios & Courtyards London',
      description: 'Create seamless outdoor living zones with cool-to-touch, UV-stable patios that pair beautifully with garden rooms and seating areas.',
      features: ['Smooth, barefoot-friendly finish', 'UV stable & colour consistent', 'Matches steps & planters', 'Designer aggregate blends'],
      link: '/resin-patios-london/',
      icon: <Palette size={32} />,
      image: '/images/resin-surfaces-bound-work2.jpeg'
    },
    {
      title: 'Resin Paving & Pathways London',
      description: 'Upgrade entrances, walkways and garden paving with slip-resistant resin that stays puddle-free and easy to maintain all year.',
      features: ['SUDS-compliant paving', 'Slip-resistant steps & ramps', 'Low-maintenance finish', 'Fast, tidy installation'],
      link: '/resin-paving-london/',
      icon: <Heart size={32} />,
      image: '/images/resin-surfaces-bound-work3.jpeg'
    }
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '10+', label: 'Years Experience' },
    { number: '5★', label: 'Average Rating' },
    { number: '100%', label: 'Customer Satisfaction' }
  ];

  const process = [
    {
      step: '01',
      title: 'Free Consultation',
      description: 'We visit your property for a free, no-obligation survey and quote'
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Create your perfect design with our expert team and detailed planning'
    },
    {
      step: '03',
      title: 'Professional Installation',
      description: 'Our experienced team installs your surface with precision and care'
    },
    {
      step: '04',
      title: 'Quality Assurance',
      description: 'Final inspection and handover with full warranty and maintenance guide'
    }
  ];

  const features = [
    {
      icon: <Award size={40} />,
      title: '10+ Years Experience',
      description: 'Expert installers with thousands of successful projects across London and surrounding areas'
    },
    {
      icon: <Shield size={40} />,
      title: 'Fully Insured',
      description: 'Comprehensive insurance and 10-year warranty on all work for complete peace of mind'
    },
    {
      icon: <Star size={40} />,
      title: '5-Star Reviews',
      description: 'Highly rated by customers across London, Surrey & Hertfordshire with verified reviews'
    },
    {
      icon: <Phone size={40} />,
      title: 'Free Quotes',
      description: 'No obligation quotes with transparent pricing, timelines, and detailed project breakdowns'
    },
    {
      icon: <Zap size={40} />,
      title: 'Fast Installation',
      description: 'Quick, efficient installation with minimal disruption to your daily routine'
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Value Added',
      description: 'Increase your property value with premium resin bound surfacing that lasts for decades'
    }
  ];

  const testimonials = [
    {
      rating: 5,
      review: "Absolutely fantastic work! Our new resin driveway looks amazing and the team was so professional. The installation was completed on time and the quality is outstanding. Highly recommend Resin Surfaces London to anyone looking for premium outdoor surfacing.",
      reviewer: "Sarah Johnson",
      location: "Richmond, Surrey",
      projectType: "Driveway Installation"
    },
    {
      rating: 5,
      review: "The resin pathway around our pool is perfect. It's safe, looks great, and has been completely maintenance-free for 2 years now. The team was knowledgeable and provided excellent service throughout the project.",
      reviewer: "Michael Chen",
      location: "Hampstead, London",
      projectType: "Pool Deck"
    },
    {
      rating: 5,
      review: "Great value for money and excellent customer service. The installation was quick and clean, with no mess left behind. The final result exceeded our expectations and we couldn't be happier.",
      reviewer: "Emma Williams",
      location: "St Albans, Hertfordshire",
      projectType: "Garden Pathway"
    }
  ];

  const galleryItems = [
    {
      image: '/images/resin-surfaces-bound-work2.jpeg',
      title: 'Premium Driveway Installation',
      description: 'Modern resin bound driveway with custom aggregate finish'
    },
    {
      image: '/images/resin-surfaces-bound-work3.jpeg',
      title: 'Pool Deck Surround',
      description: 'Safe and stylish pool deck with anti-slip finish'
    },
    {
      image: '/images/resin-surfaces-bound-work4.jpeg',
      title: 'Garden Pathway Design',
      description: 'Beautiful pathway installation in residential garden'
    }
  ];

  const faqs = [
    {
      question: 'What is resin bound surfacing?',
      answer: 'Resin bound surfacing is a premium outdoor surface where natural stone aggregates are mixed with a clear resin binder and applied to create a smooth, permeable, and durable finish. It\'s perfect for driveways, patios, paving and pool areas.'
    },
    {
      question: 'How long does resin bound surfacing last?',
      answer: 'With proper installation and maintenance, resin bound surfacing can last 15-25 years. We provide a comprehensive 10-year warranty on all our installations, covering both materials and workmanship.'
    },
    {
      question: 'Is resin bound surfacing permeable?',
      answer: 'Yes! Resin bound surfacing is fully permeable and SUDS compliant. This means water drains naturally through the surface, preventing flooding and puddles, which is especially important in London\'s drainage requirements.'
    },
    {
      question: 'How much does resin bound surfacing cost?',
      answer: 'Prices typically range from £45-65 per square metre, depending on project size, site conditions, and chosen aggregate. We provide free, detailed quotes with no hidden costs and transparent pricing breakdowns.'
    },
    {
      question: 'Can resin bound be installed over existing surfaces?',
      answer: 'In most cases, yes. We can install over concrete, tarmac, and other solid surfaces. The existing surface needs to be stable and properly prepared. We\'ll assess this during our free survey and advise accordingly.'
    },
    {
      question: 'What areas do you cover?',
      answer: 'We serve all London boroughs, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire. Our service area includes Richmond, Kingston, Croydon, St Albans, Watford, Hemel Hempstead, Canterbury, Maidstone, Chelmsford, Colchester, Aylesbury, High Wycombe, Reading, Windsor, and many more locations. Contact us to confirm coverage in your specific area.'
    }
  ];

  const localAreas = [
    { name: 'London', description: 'All London Boroughs', icon: <MapPin size={24} /> },
    { name: 'Surrey', description: 'Richmond, Kingston, Croydon', icon: <MapPin size={24} /> },
    { name: 'Hertfordshire', description: 'St Albans, Watford, Hemel', icon: <MapPin size={24} /> }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Resin Driveways, Patios & Paving London | #1 Resin Bound Installers | 10+ Year Warranty | Free Quotes</title>
        <meta name="description" content="🏆 Resin Surfaces London specialises in resin bound driveways, patios & paving. Seamless, SUDS-compliant surfaces across London, Surrey & Hertfordshire with 500+ installs, ⭐⭐⭐⭐⭐ reviews, premium Vuba materials & 10+ year warranty. Driveways • Patios • Paving • Free quotes today!" />
        <meta name="keywords" content="resin driveways London, resin patios London, resin paving London, resin bound surfacing London, resin pathway installers London, resin driveway installers London, resin patio contractors London, permeable paving London, resin bound patio London, resin paving specialists London, SUDS compliant driveways, anti-slip resin patios, resin paving cost London, resin courtyard London, resin front steps London, resin driveway quotes London, premium resin patios, resin paving near me, resin driveway design London, resin patio design London, resin surfacing experts London, resin paving Westminster, resin patios Camden, resin driveways Islington, resin paving Hackney, resin patios Greenwich, resin paving Southwark, resin driveways Richmond, resin patios Kingston, resin paving Croydon, resin driveways Bromley, resin patios Bexley, resin paving Havering, resin patios Barnet, resin driveways Harrow, resin patios Hillingdon, resin paving Ealing, resin patios Hounslow, resin driveways Brent, resin patios Haringey, resin paving Newham, resin driveways Barking" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/" />
        <meta property="og:title" content="🏆 Resin Driveways, Patios & Paving London | Premium Resin Bound Specialists" />
        <meta property="og:description" content="Leading resin bound experts for driveways, patios & paving in London. 500+ installs, ⭐⭐⭐⭐⭐ reviews, Vuba materials, SUDS compliant & 10+ year warranty. Get a free quote today." />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.resinsurfaceslondon.co.uk/" />
        <meta name="twitter:title" content="Resin Driveways, Patios & Paving London | Resin Surfaces London" />
        <meta name="twitter:description" content="Driveways, patios & paving by London's resin bound specialists. 500+ installs, ⭐⭐⭐⭐⭐ reviews, premium materials & free quotes." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta name="twitter:site" content="@ResinSurfacesLdn" />
        <meta name="twitter:creator" content="@ResinSurfacesLdn" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="1 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="True" />
        <meta name="MobileOptimized" content="320" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://www.resinsurfaceslondon.co.uk/#organization",
            "name": "Resin Surfaces London",
            "alternateName": "ZAN Resin Surfaces Ltd",
            "description": "🏆 Resin bound specialists for driveways, patios and paving across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire. Partnered with Vuba Surfaces and GCL Products.",
            "url": "https://www.resinsurfaceslondon.co.uk",
            "telephone": "+44 7535 935779",
            "email": "info@resinsurfaceslondon.co.uk",
            "logo": "https://www.resinsurfaceslondon.co.uk/logo.png",
            "image": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg",
            "foundingDate": "2013",
            "numberOfEmployees": "10-20",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Broadfield Broadhurst Gardens",
              "addressLocality": "London",
              "addressRegion": "London",
              "postalCode": "NW6 3BN",
              "addressCountry": "GB"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 51.5074,
              "longitude": -0.1278
            },
            "areaServed": [
              {"@type": "City", "name": "London"},
              {"@type": "City", "name": "Surrey"},
              {"@type": "City", "name": "Hertfordshire"},
              {"@type": "City", "name": "Buckinghamshire"},
              {"@type": "City", "name": "Kent"},
              {"@type": "City", "name": "Essex"},
              {"@type": "City", "name": "Berkshire"},
              {"@type": "City", "name": "Oxfordshire"}
            ],
            "serviceType": [
              "Resin Bound Driveways",
              "Resin Bound Patios",
              "Resin Paving & Pathways",
              "Permeable Surfacing",
              "SUDS Compliant Surfacing",
              "Anti-slip Surfacing",
              "Commercial Resin Surfacing",
              "Residential Resin Driveways"
            ],
            "priceRange": "££",
            "paymentAccepted": ["Cash", "Card", "Bank Transfer"],
            "currenciesAccepted": "GBP",
            "openingHours": "Mo-Fr 08:00-18:00,Sa 09:00-16:00",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Sarah Johnson"
                },
                "datePublished": "2024-12-15",
                "reviewBody": "Absolutely fantastic work! Our new resin driveway looks amazing and the team was so professional. Highly recommend Resin Surfaces London."
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Michael Thompson"
                },
                "datePublished": "2024-12-10",
                "reviewBody": "Outstanding service from start to finish. The resin bound pathway they installed in our garden is beautiful and has held up perfectly. 5 stars!"
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Emma Williams"
                },
                "datePublished": "2024-12-05",
                "reviewBody": "Professional team, excellent workmanship. Our pool deck looks incredible and is completely slip-resistant. Worth every penny!"
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "David Brown"
                },
                "datePublished": "2024-11-28",
                "reviewBody": "Best resin driveway company in London! The quality is exceptional and the team was punctual and clean. Highly recommended!"
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Lisa Davis"
                },
                "datePublished": "2024-11-20",
                "reviewBody": "Amazing transformation of our outdoor space. The resin bound surfacing is durable, beautiful, and exactly what we wanted. 5 stars!"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Resin Bound Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Resin Driveways London",
                    "description": "Premium resin bound driveway installation with 10+ year warranty"
                  },
                  "price": "45-65",
                  "priceCurrency": "GBP",
                  "priceSpecification": {
                    "@type": "PriceSpecification",
                    "price": "45-65",
                    "priceCurrency": "GBP",
                    "unitCode": "MTK"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Resin Patios & Paving London",
                    "description": "Resin bound patios, courtyards and paving for seamless outdoor living"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Resin Pool Decks London",
                    "description": "Safe and stylish resin bound pool deck installation with anti-slip finish"
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.facebook.com/resinsurfaceslondon",
              "https://www.instagram.com/resinsurfaceslondon",
              "https://www.linkedin.com/company/resin-surfaces-london"
            ]
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://www.resinsurfaceslondon.co.uk/#website",
            "url": "https://www.resinsurfaceslondon.co.uk",
            "name": "Resin Surfaces London",
            "description": "Resin bound driveways, patios and paving specialists in London",
            "publisher": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            },
            "potentialAction": [
              {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://www.resinsurfaceslondon.co.uk/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            ]
          })}
        </script>

        {/* Additional Rating Schema for Enhanced Google Search Visibility */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Bound Driveways, Patios & Paving London",
            "description": "Resin bound driveway, patio and paving installation across London",
            "provider": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            },
            "areaServed": {
              "@type": "City",
              "name": "London"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "James Wilson"
                },
                "datePublished": "2024-12-18",
                "reviewBody": "Exceptional quality and service! Our resin driveway exceeded all expectations. The team was professional, punctual, and the final result is stunning. 5 stars!"
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Rachel Green"
                },
                "datePublished": "2024-12-12",
                "reviewBody": "Outstanding workmanship! The resin bound pathway they created is beautiful and durable. Highly recommend this company for any resin surfacing needs."
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Bound Driveways, Patios & Paving"
        subtitle="We design and install premium resin bound driveways, patios and paving across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire & Berkshire. Seamless, permeable surfaces that stay beautiful for years."
        badge="Driveways • Patios • Paving • 10+ Year Warranty"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <StatsSection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Trusted by Hundreds of Homeowners</SectionTitle>
          <SectionSubtitle style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Our track record speaks for itself with proven results across London and surrounding areas
          </SectionSubtitle>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </StatsSection>

      <Section>
        <ServicesContainer>
          <SectionTitle>Our Premium Services</SectionTitle>
          <SectionSubtitle>
            Specialist resin bound driveways, patios and paving packages built to boost kerb appeal, extend outdoor living space and keep every surface puddle-free.
          </SectionSubtitle>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <img src={service.image} alt={service.title} className="service-image" />
                <div className="service-content">
                  <div className="icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul>
                    {service.features.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <StyledButton to={service.link} className="primary">
                    Learn More <ArrowRight size={16} />
                  </StyledButton>
                </div>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </ServicesContainer>
      </Section>

      <ProcessSection>
        <Container>
          <SectionTitle>Our Simple 4-Step Process</SectionTitle>
          <SectionSubtitle>
            From initial consultation to final handover, we make the entire process smooth and transparent
          </SectionSubtitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <FeaturesSection>
        <Container>
          <SectionTitle>Why Choose Resin Surfaces London?</SectionTitle>
          <SectionSubtitle>
            Experience the difference with our commitment to quality, service, and customer satisfaction
          </SectionSubtitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      <PartnershipSection>
        <Container>
          <SectionTitle>Proud Partners with Industry Leaders</SectionTitle>
          <SectionSubtitle>
            We're proud to partner with the UK's leading manufacturers and suppliers to deliver premium quality materials and services
          </SectionSubtitle>
          <PartnershipsGrid>
            <PartnershipCard>
              <PartnershipLogo>
                <img src="/vuba_resin_logo_uk.png" alt="Vuba Surfaces - UK's #1 Resin Bound Surfacing Manufacturer" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>Vuba Surfaces</h3>
                <p className="partner-tagline">UK's #1 Resin Bound Manufacturer since 2009</p>
                <p>
                  Our partnership with <a href="https://www.vubasurfaces.co.uk/" target="_blank" rel="noopener noreferrer">Vuba Surfaces</a> 
                  ensures you receive only the highest quality resin bound materials. As the UK's leading manufacturer since 2009, 
                  Vuba provides us with premium, BBA-certified products that guarantee durability, permeability, and aesthetic excellence.
                </p>
                <PartnershipBenefits>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>BBA Certified Materials</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>15+ Years Experience</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Premium Quality Guarantee</span>
                  </PartnershipBenefit>
                </PartnershipBenefits>
                <PartnershipCTA>
                  <a 
                    href="https://www.vubasurfaces.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About Vuba <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>

            <PartnershipCard>
              <PartnershipLogo>
                <img src="/gcl_products_logo_uk.png" alt="GCL Products - Groundworks, Construction & Landscaping" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>GCL Products</h3>
                <p className="partner-tagline">Groundworks, Construction & Landscaping</p>
                <p>
                  Our partnership with <a href="https://www.gclproducts.co.uk/" target="_blank" rel="noopener noreferrer">GCL Products</a> 
                  provides access to a comprehensive range of groundworks, construction, and landscaping products. 
                  GCL Products Ltd delivers high-quality materials across the UK, ensuring reliable supply and consistent quality for all our projects.
                </p>
                <PartnershipBenefits>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>UK-Wide Delivery</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Comprehensive Product Range</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Reliable Supply Chain</span>
                  </PartnershipBenefit>
                </PartnershipBenefits>
                <PartnershipCTA>
                  <a 
                    href="https://www.gclproducts.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About GCL <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>
          </PartnershipsGrid>
        </Container>
      </PartnershipSection>

      <TestimonialsSection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>What Our Customers Say</SectionTitle>
          <SectionSubtitle style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Real reviews from real customers who have transformed their outdoor spaces with us
          </SectionSubtitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Quote className="quote-icon" />
                <div className="rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="currentColor" />
                  ))}
                </div>
                <p>"{testimonial.review}"</p>
                <div className="reviewer">{testimonial.reviewer}</div>
                <div className="location">{testimonial.location}</div>
                <div className="project-type">{testimonial.projectType}</div>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </Container>
      </TestimonialsSection>

      <GalleryPreviewSection>
        <Container>
          <SectionTitle>Our Recent Work</SectionTitle>
          <SectionSubtitle>
            Explore some of our latest projects showcasing the quality and beauty of our resin bound installations
          </SectionSubtitle>
          <GalleryGrid>
            {galleryItems.map((item, index) => (
              <Link to="/gallery/" key={index} style={{ textDecoration: 'none' }}>
                <GalleryCard
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <img src={item.image} alt={item.title} />
                  <div className="overlay">
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                </GalleryCard>
              </Link>
            ))}
          </GalleryGrid>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <StyledButton to="/gallery/" className="primary">
              View Full Gallery <ArrowRight size={20} />
            </StyledButton>
          </div>
        </Container>
      </GalleryPreviewSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <SectionSubtitle>
            Get answers to the most common questions about resin bound surfacing
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <LocalAreasSection>
        <Container>
          <SectionTitle>Areas We Cover</SectionTitle>
          <SectionSubtitle>
            Resin bound driveway, patio and paving installations across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire
          </SectionSubtitle>
          <AreasGrid>
            {localAreas.map((area, index) => (
              <AreaCard
                key={area.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{area.icon}</div>
                <h4>{area.name}</h4>
                <p>{area.description}</p>
              </AreaCard>
            ))}
          </AreasGrid>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <StyledButton to="/areas-we-cover/" className="primary">
              View All Areas <ArrowRight size={20} />
            </StyledButton>
          </div>
        </Container>
      </LocalAreasSection>

      <CTA>
        <CTAContent>
          <SectionTitle style={{ color: 'white', marginBottom: '1.5rem' }}>Ready to Transform Your Outdoor Space?</SectionTitle>
          <p style={{ fontSize: '1.4rem', marginBottom: '2rem', opacity: 0.95, lineHeight: '1.6', fontWeight: '500' }}>
            Join <strong style={{ color: '#FECE1B' }}>500+ satisfied homeowners</strong> who chose us for resin bound driveways, patios and paving upgrades.
          </p>
          <p style={{ fontSize: '1.2rem', marginBottom: '3rem', opacity: 0.9, lineHeight: '1.6' }}>
            Get your <strong style={{ color: '#FECE1B' }}>free quote today</strong> and see why we’re London’s trusted specialist for seamless driveways, patios and paving with over 10 years of experience.
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="quote-button">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="whatsapp">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
            <StyledButton to="tel:+447535935779" className="secondary">
              <Phone size={20} /> Call Now
            </StyledButton>
          </CTAButtons>
          <div style={{ 
            marginTop: '3rem', 
            padding: '1.5rem 2rem', 
            background: 'rgba(254, 206, 27, 0.1)', 
            borderRadius: '15px', 
            border: '1px solid rgba(254, 206, 27, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', fontSize: '0.95rem', opacity: 0.95 }}>
              <span>✓ Free, no-obligation quotes</span>
              <span>✓ 10+ year warranty</span>
              <span>✓ Fully insured</span>
              <span>✓ 5-star rated</span>
            </div>
          </div>
        </CTAContent>
      </CTA>
    </>
  );
};

export default HomePage;